import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { TriangleAlert } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <IonPage>
          <IonContent>
            <div role="alert" className="min-h-screen bg-background flex items-center justify-center p-6">
              <div className="max-w-md w-full text-center">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 mb-5"
                >
                  <TriangleAlert className="w-8 h-8" />
                </span>
                <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">
                  Something went wrong
                </h1>
                <p className="text-muted-foreground mb-6">
                  An unexpected error occurred. You can try again or reload the page.
                </p>
                {this.state.error && (
                  <pre className="text-xs text-muted-foreground bg-surface border border-border rounded-2xl p-3 mb-6 text-left overflow-auto max-h-32">
                    {this.state.error.message}
                  </pre>
                )}
                <div className="flex gap-3 justify-center">
                  <Button onClick={this.handleRetry}>Try Again</Button>
                  <Button variant="secondary" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                </div>
              </div>
            </div>
          </IonContent>
        </IonPage>
      );
    }

    return this.props.children;
  }
}
